import React, { useCallback, useEffect } from 'react';
import { useIntl } from 'react-intl';
import type { CampaignLaunchQuestion } from 'fym-shared/src/music24/campaignLaunch';
import type { TikTokPostSettings } from 'fym-shared/src/music24/tiktokPostSettings';
import { TypedMemo } from '../../../../../../common/types';
import { useSMLRouter } from '../../../../../../../routing/sml/hooks';
import { SMLRoutes } from '../../../../../../../routing/sml/SMLRoutes';
import { SMLSettingsTabId } from '../../../../../../settings/tabs/types';
import { TikTokBlockNotes } from '../../../../composer/tiktok/TikTokBlockNotes';
import { TikTokConsentLine } from '../../../../composer/tiktok/TikTokConsentLine';
import { TikTokDisclosureFields } from '../../../../composer/tiktok/TikTokDisclosureFields';
import { TikTokInteractionFields } from '../../../../composer/tiktok/TikTokInteractionFields';
import { TikTokPrivacyField } from '../../../../composer/tiktok/TikTokPrivacyField';
import { tiktokMessages } from '../../../../composer/tiktok/tiktokMessages';
import { useTikTokSettings } from '../../../../composer/tiktok/useTikTokSettings';
import { launchMessages } from './launchMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the run sheet; each part uses its own slice of it */
import styles from '../Rollout.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** What the dialog reads from the TikTok row. */
export interface TikTokLaunchAnswer {
  /** The settings to send, or null. */
  settings: TikTokPostSettings | null;
  /** True while the dialog must wait for this row: TikTok can take an answer and has none yet. */
  waits: boolean;
}

interface LaunchTikTokQuestionProps {
  accountId: number;
  question: CampaignLaunchQuestion;
  onAnswer: (answer: TikTokLaunchAnswer) => void;
}

/**
 * The TikTok row of the campaign questions. TikTok's Content Sharing Guidelines bind it: the
 * creator is read from TikTok now, the privacy level has NO selected value, the interactions and
 * the commercial content switch start off, and the consent line shows. The user answers one time
 * for every TikTok post of the campaign; nothing is stored for the next campaign. When TikTok
 * cannot take a post now, the row says why and the other posts are scheduled without it.
 */
const LaunchTikTokQuestionComponent: React.FC<LaunchTikTokQuestionProps> = ({ accountId, question, onAnswer }) => {
  const intl = useIntl();
  const router = useSMLRouter();
  const state = useTikTokSettings({ accountId, isActive: true });
  const { draft, answer, issues, blockedReason, isReady, settings, isLoading } = state;
  const creator = answer?.creator ?? null;
  const canAnswer = creator !== null && blockedReason === null;
  const openChannels = useCallback(() => router.push(SMLRoutes.promoteChannels), [router]);
  const seePlans = useCallback(() => router.push(SMLRoutes.settings, { tab: SMLSettingsTabId.subscription }), [router]);

  useEffect(() => {
    onAnswer({ settings: isReady ? settings : null, waits: isLoading || (canAnswer && !isReady) });
  }, [canAnswer, isLoading, isReady, onAnswer, settings]);

  return (
    <section className={styles.launchQuestion} data-testid="launch-question-tiktok">
      <h3 className={styles.launchQuestionHead}>
        {intl.formatMessage(launchMessages.questionPosts, { name: 'TikTok', count: question.postCount })}
      </h3>
      {creator === null && isLoading ? (
        <p className={styles.launchNote}>{intl.formatMessage(tiktokMessages.loading)}</p>
      ) : null}
      <TikTokBlockNotes
        blockedReason={blockedReason}
        issues={issues}
        durationSec={null}
        maxDurationSec={null}
        canSeePlans
        onCheckAgain={state.reload}
        onOpenChannels={openChannels}
        onSeePlans={seePlans}
      />
      {!canAnswer && !isLoading ? (
        <p className={styles.launchNote}>{intl.formatMessage(launchMessages.tiktokLater)}</p>
      ) : null}
      {creator === null || !canAnswer ? null : (
        <>
          <TikTokPrivacyField draft={draft} options={creator.privacyLevelOptions} onChange={state.setPrivacy} />
          <TikTokInteractionFields
            creator={creator}
            allowComment={draft.allowComment}
            allowDuet={draft.allowDuet}
            allowStitch={draft.allowStitch}
            mediaKind={question.hasVideo ? 'video' : 'image'}
            onAllowComment={state.setAllowComment}
            onAllowDuet={state.setAllowDuet}
            onAllowStitch={state.setAllowStitch}
          />
          <TikTokDisclosureFields
            draft={draft}
            onDiscloseContent={state.setDiscloseContent}
            onYourBrand={state.setYourBrand}
            onBrandedContent={state.setBrandedContent}
          />
          <TikTokConsentLine draft={draft} />
          <p className={styles.launchNote}>{intl.formatMessage(launchMessages.tiktokEachTime)}</p>
        </>
      )}
    </section>
  );
};

export const LaunchTikTokQuestion = TypedMemo(LaunchTikTokQuestionComponent);
